import React, { useEffect } from 'react';
import { X, Calendar, Clock, ExternalLink } from 'lucide-react';
import { BlogPost } from '../../types/blog';

interface BlogPopupProps {
  post: BlogPost;
  onClose: () => void;
}

export const BlogPopup: React.FC<BlogPopupProps> = ({ post, onClose }) => {
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const shareUrl = `${window.location.origin}/blog/${post.id}`;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.hook,
          url: shareUrl,
        });
      } catch (err) {
        // Fallback to copying to clipboard
        navigator.clipboard.writeText(shareUrl);
      }
    } else {
      // Fallback to copying to clipboard
      navigator.clipboard.writeText(shareUrl);
    }
  };

  // Prevent body scroll when popup is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-gray-900 rounded-lg shadow-2xl border border-gray-800 overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800 p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Calendar size={16} />
              <time dateTime={post.publishedAt}>
                {formatDate(post.publishedAt)}
              </time>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Clock size={16} />
              <span>{post.readingTime} min read</span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handleShare}
              className="flex items-center gap-2 px-4 py-2 bg-green-400 text-black rounded-lg hover:bg-green-300 transition-colors duration-300 text-sm font-medium"
            >
              <ExternalLink size={16} />
              Share
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors duration-300"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Hero Image */}
          <div className="relative h-64 md:h-80 bg-gray-800">
            <picture>
              <source 
                srcSet={post.fullImageWebp || post.image} 
                type="image/webp" 
              />
              <img 
                src={post.fullImageJpg || post.image} 
                alt={post.title}
                className="w-full h-full object-contain bg-gray-800"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'https://images.unsplash.com/photo-1639322537228-f710d846310a?auto=format&fit=crop&w=800&h=400';
                }}
              />
            </picture>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          </div>
          
          {/* Article Content */}
          <div className="p-6 md:p-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight text-center">
              {post.title}
            </h1>
            
            <p className="text-lg text-gray-300 mb-6 leading-relaxed text-left max-w-3xl mx-auto">
              {post.hook}
            </p>
            
            <div 
              className="max-w-3xl mx-auto px-4 text-left leading-relaxed space-y-6 mb-8 prose prose-lg prose-invert prose-green"
              dangerouslySetInnerHTML={{ __html: post.content }}
              style={{
                '--tw-prose-body': 'rgb(209 213 219)',
                '--tw-prose-headings': 'rgb(255 255 255)',
                '--tw-prose-lead': 'rgb(156 163 175)',
                '--tw-prose-links': 'rgb(74 222 128)',
                '--tw-prose-bold': 'rgb(255 255 255)',
                '--tw-prose-counters': 'rgb(156 163 175)',
                '--tw-prose-bullets': 'rgb(107 114 128)',
                '--tw-prose-hr': 'rgb(55 65 81)',
                '--tw-prose-quotes': 'rgb(243 244 246)',
                '--tw-prose-quote-borders': 'rgb(55 65 81)',
                '--tw-prose-captions': 'rgb(156 163 175)',
                '--tw-prose-code': 'rgb(243 244 246)',
                '--tw-prose-pre-code': 'rgb(229 231 235)',
                '--tw-prose-pre-bg': 'rgb(17 24 39)',
                '--tw-prose-th-borders': 'rgb(55 65 81)',
                '--tw-prose-td-borders': 'rgb(75 85 99)'
              } as React.CSSProperties}
            />
            
            {/* Tags Section */}
            <div className="max-w-3xl mx-auto">
              <div className="border-t border-gray-700 pt-6">
                <h3 className="text-lg font-semibold text-white mb-3">Related Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-green-400/20 text-green-400 rounded-full text-sm font-medium border border-green-400/30 hover:bg-green-400/30 transition-colors duration-300"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};